import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, BehaviorSubject, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';

import { ILogin, ISignUp, IUser, IUserAuth, LGEDataService } from '..';
import { CCSession } from '../interfaces/user.interface';
import { CorpService } from './corp.service';
import { AppStorageService } from './storage.service';

@Injectable()
export class AuthenticationService {
  private _CC_API_CONTEXT: string;
  private _AUTH_USER_ACTION: string;
  private _VALIDATE_SID_ACTION: string;
  private _VALIDATE_SID_ROLES_ACTION: string;
  private _CURRENT_SESSION_KEY: string;
  public currentSession = new BehaviorSubject<any>({});

  constructor(
    private _corpService: CorpService,
    private http: HttpClient,
    private storage: AppStorageService
  ) {
    this._CC_API_CONTEXT = '/APICorpCommon/AppData/'; //environment.EXTERNAL_APP ? '/APICorpCommon/AppData/' : '/APICorpCommonAnon/AppData/';
    this._AUTH_USER_ACTION = 'AuthenticateUserPost';
    this._VALIDATE_SID_ACTION = 'validateSID';
    this._VALIDATE_SID_ROLES_ACTION = 'validateSIDWithRoles';
    this._CURRENT_SESSION_KEY = 'CURRENT_SESSION';
  }
  public checkAuthentication(user: IUserAuth): Observable<boolean | Error> {
    let userSession = user as IUser;
    return this.validateSession(userSession);
  }

  public getSession = () => {
    let session: CCSession;
    session = this.storage.get(
      `${environment.APPLICATION_ID}_${this._CURRENT_SESSION_KEY}`,
      true
    ) as CCSession;
    // this.validateSession(session).pipe(
    //   switchMap(res => {
    //     if (res)
    //       return session;
    //     else
    //       return this.SingleSignOn();
    //   })
    // )
    return session;
  };

  public saveSession = (session) => {
    this.storage.save(
      `${environment.APPLICATION_ID}_${this._CURRENT_SESSION_KEY}`,
      session
    );
  };

  public removeSession = () => {
    this.storage.delete(
      `${environment.APPLICATION_ID}_${this._CURRENT_SESSION_KEY}`
    );
  };

  // public SingleSignOn = () => {
  //   this.storage.save(`${environment.APPLICATION_ID}${this._CURRENT_SESSION_KEY}`, session);
  // }
  private formDataFactory = (user?: ILogin) => {
    const formData: FormData = new FormData();
    let session = this.getSession();
    formData.append('userId', user?.username || session?.userId);
    formData.append('password', user?.password || '');
    formData.append('applicationID', environment.APPLICATION_ID);
    return formData;
  };
  public SingleSignOn = () => {
    let inputSession: FormData = this.formDataFactory();
    let actionName = this._AUTH_USER_ACTION;
    let url = environment.API_URL.concat(this._CC_API_CONTEXT, actionName);
    return this.http
      .post(url, inputSession, { withCredentials: true, responseType: 'json' })
      .pipe(
        map((res: any) => {
          if (res.message.length > 0) {
            throw new Error(res.message);
          } else if (!this.isSessionValid(res)) {
            throw new Error(
              'An invalid session was retrieved.\n\n'.concat(res.message)
            );
          } else {
            this.saveSession(res.session);
            return res.session;
          }
        }),
        catchError((err) => {
          console.error(err);
          // alert('single sign-on failed.');
          return of(err);
        })
      );
    // .then(parseResult, function (error) { return _this.rejectError(reject, error); });
  };

  public authenticateUser(data: ILogin): Observable<IUserAuth> {
    const formData: FormData = this.formDataFactory(data);
    let url = environment.API_URL.concat(
      this._CC_API_CONTEXT,
      this._AUTH_USER_ACTION
    );
    return this.http
      .post(url, formData, {
        withCredentials: true,
        responseType: 'json' as 'json',
      })
      .pipe(
        map((res: any) => {
          if (res.message.length > 0) {
            throw new Error(res.message);
          } else if (!this.isSessionValid(res)) {
            throw new Error(
              'An invalid session was retrieved.\n\n'.concat(res.message)
            );
          } else {
            let userAuth = res.session as IUserAuth;
            console.info('Logged in, redirecting!');
            this.saveSession(res.session);
            return userAuth;
          }
        })
      );

    // console.info('Attempting to log in...');
    // return this.authenticate().pipe(
    //   map((data) => {
    //     let userAuth = data as IUserAuth;
    //     console.info('Logged in, redirecting!')
    //     this.currentSession = data;
    //     return userAuth;
    //   })
    // );
  }
  public isSessionValid = (ccSession) => {
    return (
      ccSession.session !== null &&
      ccSession.session.active === true &&
      ccSession.message.toLowerCase().indexOf('session is not active.') === -1
    );
  };
  public authenticate = (authData) => {
    let _this = this;
    let url = environment.API_URL.concat(
      _this._CC_API_CONTEXT,
      _this._AUTH_USER_ACTION
    );
    return this.http.post(url, authData).pipe(
      map((res: any) => {
        if (res.message.length > 0) {
          return new Error(res.message);
        } else if (!this.isSessionValid(res)) {
          return new Error('An invalid session was retrieved.');
        } else {
          let timeTaggedSession = Object.assign({}, res.session, {
            lastLoginTime: new Date(),
          });
          return timeTaggedSession;
        }
      }),
      catchError((error) => {
        console.error(error);
        return of(error);
      })
    );
  };
  validateSession(userSession?): Observable<boolean> {
    const session = userSession ?? this.getSession();
    if (!session || session === null) {
      return of(false);
    } else {
      const formData: FormData = new FormData();
      formData.append('userId', session.userId || session.userID);
      formData.append('guid', session.guid);
      formData.append('applicationID', session.applicationID);
      let _this = this;
      let url = environment.API_URL.concat(
        _this._CC_API_CONTEXT,
        _this._VALIDATE_SID_ROLES_ACTION
      );

      return this.http.post(url, formData, { withCredentials: true }).pipe(
        map((response: any) => {
          const result = this.isSessionValid(response);
          if (result) 
            this.saveSession(response.session);
          return result;
        })
      );
    }
  }
  // public validateSession = (session?): Observable<boolean | Error> => {
  //   return this.sendValidateRequest(session, false).pipe(
  //     switchMap(res => {
  //       if (res.errorExist !== undefined) {
  //         if (res.errorExists) {
  //           return throwError(() => new Error(res.message))
  //         }
  //         else if (res.session !== undefined &&
  //           res.session.active !== undefined &&
  //           !res.session.active) {
  //           return throwError(() => new Error('Session is Not Active.'))
  //         }
  //         else {
  //           return of(true);
  //         }
  //       } else
  //         return throwError(() => new Error('Server result was not in an expected format'));
  //     }),
  //     catchError(err => of(err))
  //   )
  // };

  private buildBaseInputSession = (inputSession?) => {
    if (inputSession === undefined) {
      return {
        sid: undefined,
        userId: undefined,
        applicationID: environment.APPLICATION_ID,
      };
    }
    return {
      sid: inputSession.sid || inputSession.guid,
      userId: inputSession.userId || inputSession.userID,
      applicationID:
        inputSession.applicationID ||
        inputSession.applicationId ||
        inputSession.appID,
    };
  };

  private sendValidateRequest = (
    session,
    shouldUpdateRoles = false
  ): Observable<any> => {
    let _this = this;
    // if (shouldUpdateRoles === void 0) { shouldUpdateRoles = false; }

    let providedSession = session !== undefined ? session : this.getSession();
    if (providedSession === undefined) {
      return of(Error('No Session Available.'));
    } else {
      let inputSession = this.buildBaseInputSession(providedSession);
      let actionName = shouldUpdateRoles
        ? _this._AUTH_USER_ACTION
        : _this._VALIDATE_SID_ACTION;
      let url = environment.API_URL.concat(_this._CC_API_CONTEXT, actionName);
      var dataOptions = {
        data: inputSession,
        url: url,
      };
      return this._corpService.postForm(dataOptions).pipe(
        map((res: any) => {
          if (
            providedSession.lastLoginTime !== undefined &&
            providedSession.lastLoginTime !== null
          ) {
            res.session.lastLoginTime = providedSession.lastLoginTime;
          }
          return res;
        }),
        catchError((error: Error) => of(error))
      );
      // }, function (error) { return _this.rejectError(reject, error); });
    }
  };
}
